import { useCallback, useEffect, useState } from "react";

/**
 * useSubjectLock — a tiny hook wrapping the session-scoped "subject lock"
 * that Studio writes to `sessionStorage["geneo:subjectLock"]`. Any page can
 * pull the same lock (Storyboard, Avatar) so the character/object identity
 * flows across the app.
 *
 * Cross-tab: listens for the `storage` event (fires for other tabs only).
 * Same-tab:  listens for the custom `geneo:subjectLockChanged` event that
 *            `setLock` dispatches on every write.
 */
const KEY = "geneo:subjectLock";
const CLEARED_KEY = "geneo:subjectLockCleared";
const EVT = "geneo:subjectLockChanged";

const readLock = () => {
  try { const raw = sessionStorage.getItem(KEY); return raw ? JSON.parse(raw) : null; }
  catch { return null; }
};

export function useSubjectLock() {
  const [lock, setLockState] = useState(readLock);

  const write = useCallback((next) => {
    setLockState(next);
    try {
      if (next) {
        sessionStorage.setItem(KEY, JSON.stringify(next));
        sessionStorage.removeItem(CLEARED_KEY);
      } else {
        sessionStorage.removeItem(KEY);
      }
    } catch {}
    window.dispatchEvent(new CustomEvent(EVT));
  }, []);

  const clear = useCallback(() => {
    setLockState(null);
    try {
      sessionStorage.removeItem(KEY);
      sessionStorage.setItem(CLEARED_KEY, "1");
    } catch {}
    window.dispatchEvent(new CustomEvent(EVT));
  }, []);

  useEffect(() => {
    const onChange = () => setLockState(readLock());
    window.addEventListener("storage", onChange);
    window.addEventListener(EVT, onChange);
    return () => {
      window.removeEventListener("storage", onChange);
      window.removeEventListener(EVT, onChange);
    };
  }, []);

  return { lock, setLock: write, clearLock: clear };
}
